import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    RiStore2Line,
    RiBarcodeLine,
    RiDatabase2Line,
    RiShieldUserLine,
    RiCheckboxCircleLine,
    RiCalendarLine,
    RiHistoryLine,
    RiGobletLine,
    RiFileCopyLine,
    RiCheckLine,
} from 'react-icons/ri';
import DashboardTooltip from '../Components/DashboardTooltip';

const ProposalDetailsCard = ({ proposal, isLoading = false }) => {
    const [copiedField, setCopiedField] = useState(null);

    // Direct API mappings
    const clientName = proposal?.display_client_name || null;
    const code = proposal?.restaurant?.code || null;
    const zohoId = proposal?.zoho_id || null;

    const hasClient = Boolean(clientName && clientName !== "null" && clientName !== "N/A");
    const hasCode = Boolean(code && code !== "null" && code !== "N/A");
    const hasZohoId = Boolean(zohoId && zohoId !== "null" && zohoId !== "N/A");
    const clientType = proposal?.client_type || "";
    const isLead = String(clientType).toLowerCase() === "lead";
    const status = String(proposal?.status || "").toUpperCase();
    const totalWines = proposal?.proposal_wines?.length || 0;

    const createdAt = proposal?.created_at && dayjs(proposal.created_at).isValid()
        ? dayjs(proposal.created_at).format("DD-MM-YYYY")
        : "N/A";

    const updatedAt = proposal?.updated_at && dayjs(proposal.updated_at).isValid()
        ? dayjs(proposal.updated_at).format("DD-MM-YYYY")
        : "N/A";

    // 1-Click Clipboard copy helper
    const handleCopy = async (text, fieldKey) => {
        if (!text || text === "N/A") return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(text));
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = String(text);
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(null), 2000);
        } catch {
            // Silently fallback if clipboard access restricted
        }
    };

    // Client Type Badge
    const renderClientTypeBadge = () => {
        if (!clientType) return <span className="text-gray-400 text-xs font-mono">N/A</span>;
        const badgeClasses = isLead
            ? "bg-gradient-to-r from-orange-50 via-amber-50 to-orange-100 text-orange-950 border-orange-300 shadow-2xs"
            : "bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-100 text-blue-950 border-blue-300 shadow-2xs";

        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10.5px] sm:text-[11px] font-semibold tracking-wider uppercase rounded-full border poppins-medium mt-1 transition-all ${badgeClasses}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isLead ? 'bg-orange-500' : 'bg-blue-600'}`} />
                {clientType}
            </span>
        );
    };

    // Status Badge
    const renderStatusBadge = () => {
        if (!status) return <span className="text-gray-400 text-xs font-mono">N/A</span>;
        let badgeClass = "bg-gray-50 text-gray-800 border-gray-200";

        if (status === "PENDING") {
            badgeClass = "bg-amber-50 text-amber-800 border-amber-200";
        } else if (status === "ORDERED") {
            badgeClass = "bg-emerald-50 text-emerald-800 border-emerald-200";
        } else if (status === "ACCEPTED") {
            badgeClass = "bg-purple-50 text-purple-800 border-purple-200";
        } else if (status === "CANCELLED") {
            badgeClass = "bg-rose-50 text-rose-800 border-rose-200";
        }

        return (
            <span className={`inline-flex items-center justify-center px-2.5 py-[3px] text-[11px] font-semibold tracking-wide uppercase rounded-md border poppins-medium mt-1 transition-all ${badgeClass}`}>
                {status}
            </span>
        );
    };

    // Shimmer Skeleton during fetch
    if (isLoading) {
        return (
            <div className="w-full bg-white rounded-2xl border-[1.5px] border-[#8B2433]/15 shadow-sm p-5 sm:p-6 mb-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[2.5px] bg-[#8B2433]/30" />
                <SkeletonTheme baseColor="#E6DCDF" highlightColor="#FFFFFF">
                    <div className="pb-4 mb-5 border-b border-[#8B2433]/10">
                        <Skeleton width={190} height={20} borderRadius={6} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(8)].map((_, idx) => (
                            <div key={`skel-card-${idx}`} className="p-4 rounded-xl border border-[#8B2433]/10 bg-gradient-to-br from-white to-[#FAF3F4]/40">
                                <div className="flex items-start gap-3">
                                    <Skeleton width={44} height={44} borderRadius={12} />
                                    <div className="flex-1">
                                        <Skeleton width={75} height={12} borderRadius={4} />
                                        <Skeleton width="85%" height={18} borderRadius={4} className="mt-2" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </SkeletonTheme>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl border-[1.5px] border-[#8B2433]/15 shadow-[0_6px_25px_-4px_rgba(139,36,51,0.06)] p-5 sm:p-6 mb-6 relative overflow-hidden transition-all duration-300">
            {/* Top Brand Wine Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#8B2433] via-[#A32D3F] to-[#8B2433]/30" />

            {/* Header: Title */}
            <div className="pb-4 mb-5 border-b border-[#8B2433]/12">
                <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#8B2433]" />
                    <h4 className="text-base sm:text-lg font-bold poppins-semibold text-[#8B2433] tracking-tight">
                        Riepilogo Dettagli Proposal
                    </h4>
                </div>
            </div>

            {/* 8 Information Tiles with Hover Highlight Micro-Interactions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
                
                {/* 1. Cliente / Ristorante */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiStore2Line className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Cliente / Ristorante
                        </span>
                        <div className="mt-1 flex items-center justify-between gap-1.5 min-w-0">
                            <div className="text-[12px] sm:text-[13px] font-bold text-[#1F2421] group-hover:text-[#8B2433] transition-colors duration-200 poppins-semibold leading-snug flex-1 min-w-0 truncate !max-w-none">
                                <DashboardTooltip 
                                    text={clientName} 
                                    maxLength={14} 
                                    alwaysShow={true} 
                                    className="whitespace-nowrap truncate !max-w-none" 
                                />
                            </div>
                            {hasClient && (
                                <button
                                    type="button"
                                    onClick={() => handleCopy(clientName, "client")}
                                    title={copiedField === "client" ? "Copiato!" : "Copia Cliente / Ristorante"}
                                    className="p-1 rounded-md bg-white border border-gray-200 hover:border-[#8B2433] hover:text-[#8B2433] text-gray-500 shadow-2xs transition-all cursor-pointer shrink-0 active:scale-95"
                                >
                                    {copiedField === "client" ? (
                                        <RiCheckLine className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                        <RiFileCopyLine className="w-3.5 h-3.5" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. Codice */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiBarcodeLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Codice
                        </span>
                        <div className="mt-1 flex items-center justify-between gap-1.5 min-w-0">
                            <span className="font-mono text-[11px] sm:text-xs font-bold text-[#1F2421] group-hover:text-[#8B2433] transition-colors duration-200 tracking-tight flex-1 min-w-0 truncate !max-w-none">
                                {code || "N/A"}
                            </span>
                            {hasCode && (
                                <button
                                    type="button"
                                    onClick={() => handleCopy(code, "code")}
                                    title={copiedField === "code" ? "Copiato!" : "Copia Codice"}
                                    className="p-1 rounded-md bg-white border border-gray-200 hover:border-[#8B2433] hover:text-[#8B2433] text-gray-500 shadow-2xs transition-all cursor-pointer shrink-0 active:scale-95"
                                >
                                    {copiedField === "code" ? (
                                        <RiCheckLine className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                        <RiFileCopyLine className="w-3.5 h-3.5" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. Zoho ID */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiDatabase2Line className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Zoho ID
                        </span>
                        <div className="mt-1 flex items-center justify-between gap-1.5 min-w-0">
                            <span className="text-[11px] sm:text-[11.5px] font-mono font-bold text-[#1F2421] group-hover:text-[#8B2433] transition-colors duration-200 tracking-tight flex-1 min-w-0 truncate !max-w-none">
                                {zohoId || "N/A"}
                            </span>
                            {hasZohoId && (
                                <button
                                    type="button"
                                    onClick={() => handleCopy(zohoId, "zoho")}
                                    title={copiedField === "zoho" ? "Copiato!" : "Copia Zoho ID"}
                                    className="p-1 rounded-md bg-white border border-gray-200 hover:border-[#8B2433] hover:text-[#8B2433] text-gray-500 shadow-2xs transition-all cursor-pointer shrink-0 active:scale-95"
                                >
                                    {copiedField === "zoho" ? (
                                        <RiCheckLine className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                        <RiFileCopyLine className="w-3.5 h-3.5" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 4. Client Type */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiShieldUserLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Client Type
                        </span>
                        <div className="mt-1">
                            {renderClientTypeBadge()}
                        </div>
                    </div>
                </div>

                {/* 5. Stato */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiCheckboxCircleLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Stato
                        </span>
                        <div className="mt-1">
                            {renderStatusBadge()}
                        </div>
                    </div>
                </div>

                {/* 6. Vini Totali */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiGobletLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Vini Totali
                        </span>
                        <div className="mt-1">
                            <span className="inline-flex items-center gap-1.5 px-3 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-[#8B2433] to-[#A32D3F] text-white shadow-2xs group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.35)] group-hover:scale-105 transition-all duration-200 poppins-semibold">
                                <RiGobletLine className="w-3.5 h-3.5" />
                                {totalWines}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 7. Data Creazione */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiCalendarLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate">
                            Data Creazione
                        </span>
                        <span className="mt-1 block text-[11px] sm:text-xs font-mono font-bold text-gray-700 group-hover:text-[#8B2433] transition-colors duration-200 truncate !max-w-none">
                            {createdAt}
                        </span>
                    </div>
                </div>

                {/* 8. Data Aggiornamento */}
                <div className="bg-gradient-to-br from-white via-[#FFFBFB] to-[#FAF3F4]/60 rounded-xl p-3.5 sm:p-4 border-[1.5px] border-[#8B2433]/12 hover:border-[#8B2433]/40 hover:-translate-y-1 hover:shadow-[0_10px_22px_-4px_rgba(139,36,51,0.14)] transition-all duration-200 ease-out flex items-start gap-3 cursor-default group">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 text-[#8B2433] shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] group-hover:shadow-[0_4px_12px_rgba(139,36,51,0.3)] transition-all duration-200 mt-0.5">
                        <RiHistoryLine className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-[#8B2433] transition-colors duration-200 poppins-medium block truncate !max-w-none">
                            Data Aggiornamento
                        </span>
                        <span className="mt-1 block text-[11px] sm:text-xs font-mono font-bold text-gray-700 group-hover:text-[#8B2433] transition-colors duration-200 truncate !max-w-none">
                            {updatedAt}
                        </span>
                    </div>
                </div>

            </div>
        </div>
    );
};

ProposalDetailsCard.propTypes = {
    proposal: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        display_client_name: PropTypes.string,
        client_type: PropTypes.string,
        zoho_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
        restaurant: PropTypes.shape({
            code: PropTypes.string,
        }),
        proposal_wines: PropTypes.arrayOf(PropTypes.object),
    }),
    isLoading: PropTypes.bool,
};

export default ProposalDetailsCard;
